import React, { useState, useRef, useEffect } from 'react';
import { toPolarCoordinates, getIndexFromPhi } from './utils';

const DraggableBall = ({ onIndexChange, onDistanceChange, thru, setPositionRef, disabled, position, setPosition, Ndir }) => {
    const containerRef = useRef(null);
    const isDraggingRef = useRef(false);

    const ballRadius = 15; // ボールの半径を指定
    const smallCircleRadius = 40; // 小さな円の半径を指定

    // スナップポイントの座標（小さな円の内側に収まるように調整）
    const leftSnapPosition = { x: -smallCircleRadius / 2 - 5, y: 0 };
    const rightSnapPosition = { x: smallCircleRadius / 2 + 5, y: 0 };
    const snapThreshold = 5; // スナップするための距離しきい値

    useEffect(() => {
        setPositionRef.current = position;
    }, [position, setPositionRef]);

    const handleMouseDown = () => {
        if (!thru && !disabled) {
            isDraggingRef.current = true;
        }
    };

    const handleMouseUp = () => {
        isDraggingRef.current = false;
    };

    const handleMouseMove = (e) => {
        if (isDraggingRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const centerX = containerRect.width / 2;
            const centerY = containerRect.height / 2;
            const newX = e.clientX - containerRect.left - centerX;
            const newY = -(e.clientY - containerRect.top - centerY); // Y軸を反転

            const distanceFromCenter = Math.sqrt(newX * newX + newY * newY);

            // スナップポイントに近い場合、スナップ位置に設定
            if (Math.abs(newX - leftSnapPosition.x) < snapThreshold && Math.abs(newY - leftSnapPosition.y) < snapThreshold) {
                handlePositionUpdate(leftSnapPosition, Ndir + 1);
            } else if (Math.abs(newX - rightSnapPosition.x) < snapThreshold && Math.abs(newY - rightSnapPosition.y) < snapThreshold) {
                handlePositionUpdate(rightSnapPosition, Ndir + 2);
            } else if (distanceFromCenter < smallCircleRadius) {
                // カーソルが小さな円の内側にある場合、円周上にボールを配置
                const angle = Math.atan2(newY, newX);
                const constrainedX = smallCircleRadius * Math.cos(angle);
                const constrainedY = smallCircleRadius * Math.sin(angle);

                handlePositionUpdate({ x: constrainedX, y: constrainedY });
            } else {
                // ボールがコンテナ内に留まるように制約
                const boundedX = Math.max(-centerX + ballRadius, Math.min(newX, centerX - ballRadius));
                const boundedY = Math.max(-centerY + ballRadius, Math.min(newY, centerY - ballRadius));

                handlePositionUpdate({ x: boundedX, y: boundedY });
            }
        }
    };

    const handlePositionUpdate = ({ x, y }, customIndex = null) => {
        setPosition({ x, y });

        if (customIndex !== null) {
            onIndexChange(customIndex);
        } else {
            const polarCoordinates = toPolarCoordinates(x, y);
            const polarIndex = getIndexFromPhi(polarCoordinates.phi, Ndir);
            onIndexChange(polarIndex);
            onDistanceChange(polarCoordinates.r.toFixed(2));
        }
    };

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('mousemove', handleMouseMove);

        // カスタムイベントリスナーを追加（ball描画）
        const handleUpdatePosition = (event) => {
            setPosition(event.detail);
        };
        window.addEventListener('updatePosition', handleUpdatePosition);

        // カスタムイベントリスナーを追加（リセット）
        const handleResetPosition = (event) => {
            handlePositionUpdate(event.detail);
        };
        window.addEventListener('resetPosition', handleResetPosition);

        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('updatePosition', handleUpdatePosition);
            window.removeEventListener('resetPosition', handleResetPosition);
        };
    }, []);

    const renderSmallCircle = () => {
        return (
            <div
                style={{
                    position: 'absolute',
                    left: `calc(50% - ${smallCircleRadius}px)`,
                    top: `calc(50% - ${smallCircleRadius}px)`,
                    width: `${smallCircleRadius * 2}px`,
                    height: `${smallCircleRadius * 2}px`,
                    borderRadius: '50%',
                    border: '2px solid #000',
                    backgroundColor: '#111',
                    opacity: 0.5,
                }}
            ></div>
        );
    };

    const renderMarkers = () => {
        return (
            <>
                <div
                    style={{
                        position: 'absolute',
                        left: `calc(50% - ${smallCircleRadius / 2 + 5}px - ${ballRadius}px)`,
                        top: `calc(50% - ${ballRadius}px)`,
                        width: `${ballRadius * 2}px`,
                        height: `${ballRadius * 2}px`,
                        borderRadius: '50%',
                        border: '2px dotted #ff00ff',
                        backgroundColor: '#9090C1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '9pt',
                    }}
                >
                    L
                </div>
                <div
                    style={{
                        position: 'absolute',
                        left: `calc(50% + ${smallCircleRadius / 2 + 5}px - ${ballRadius}px)`,
                        top: `calc(50% - ${ballRadius}px)`,
                        width: `${ballRadius * 2}px`,
                        height: `${ballRadius * 2}px`,
                        borderRadius: '50%',
                        border: '2px dotted #ff00ff',
                        backgroundColor: '#9090C1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '9pt',
                    }}
                >
                    R
                </div>
            </>
        );
    };

    let className = "draggable-circle";
    if (thru) {
        className += " disabled";
    } else if (disabled) {
        className += " preview";
    }

    return (
        <div className="circle-container" ref={containerRef}>
            <div className="label top-label">Front</div>
            <div className="label bottom-label">Back</div>
            <div className="label left-label">Left</div>
            <div className="label right-label">Right</div>
            <div className="inner-circle">
                <div className="crosshair"></div>
                {renderSmallCircle()}
                {renderMarkers()}
            </div>
            <div
                className={className}
                onMouseDown={handleMouseDown}
                style={{
                    left: `calc(50% + ${position.x}px - ${ballRadius}px)`,
                    top: `calc(50% - ${position.y}px - ${ballRadius}px)` // Y軸を反転して位置調整
                }}
            ></div>
        </div>
    );
};

export default DraggableBall;
